import { formatFeaturedRow } from '@iheartjane/menu';
import {
  Button,
  Flex,
  Typography,
  useMobileMediaQuery,
} from '@iheartjane/reefer';
import { sentenceCase } from '@iheartjane/utils';
import { useParams, useSearchParams } from 'react-router';

import {
  CATEGORY_DESCRIPTIONS,
  formatFeaturedCategory,
} from '@jane/search/util';
import { MenuBreadcrumbs } from '@jane/shared-ecomm/components';
import { useMenuRowWeightFilter } from '@jane/shared-ecomm/hooks';
import { useMenu } from '@jane/shared-ecomm/providers';
import { useJaneUserId } from '@jane/shared/data-access';
import type { MenuCategoryPath } from '@jane/shared/types';
import { insertIfArr } from '@jane/shared/util';

import { useBuyAgain } from '../../hooks/useBuyAgain';
import { useComposeMenuFilters } from '../../hooks/useComposeMenuFilters';
import { MenuFiltersAndProductsGrid } from '../../MenuComponents/MenuFiltersAndProductsGrid/MenuFiltersAndProductsGrid';
import { MenuPageSeo } from '../../MenuComponents/MenuSeo/MenuPageSeo';
import { MENU_CATEGORY_TABS } from '../../MenuComponents/MenuTabs/menuCategoryTabs';
import { MenuTitle } from '../../MenuComponents/MenuTitle/MenuTitle';

export const MenuProducts = ({
  initialCategory,
}: {
  initialCategory?: MenuCategoryPath;
}) => {
  const {
    category: categoryParam = 'all',
    featured_category: featuredCategory,
    rowId: rowIdParam,
    slug,
  } = useParams<{
    category: MenuCategoryPath;
    featured_category: string;
    rowId: string;
    slug: string;
  }>();
  const [queryParams] = useSearchParams();
  const hideSponsored = queryParams.get('hide_sponsored');

  const isMobile = useMobileMediaQuery({});
  const {
    appInfo: { appMode },
    paths,
    store,
  } = useMenu();
  const userId = useJaneUserId();

  const category = initialCategory ?? categoryParam;

  const customRowSettings = store.custom_row_settings;

  const { featuredTitle, featuredFilter, isJaneDefined } = rowIdParam
    ? formatFeaturedRow(Number(store.id), customRowSettings, Number(rowIdParam))
    : formatFeaturedCategory(store, featuredCategory);

  const weightFilter = useMenuRowWeightFilter({
    customRowSettings,
    rowId: rowIdParam ? Number(rowIdParam) : undefined,
    rowTitle: !rowIdParam ? featuredTitle || featuredCategory : undefined,
  });

  const hasFeaturedRow = !!(featuredCategory || rowIdParam);
  const filterByCategory =
    !['all', 'featured'].includes(category) && MENU_CATEGORY_TABS[category];
  const isBuyAgain =
    featuredCategory === 'buy-again' || (!!rowIdParam && slug === 'buy-again');
  const filterByFeaturedCategory =
    hasFeaturedRow && !isBuyAgain && featuredFilter.length;
  const filterByBuyAgain = isBuyAgain;

  const { purchasedProductFilter } = useBuyAgain({
    userId: filterByBuyAgain ? userId : undefined,
  });

  const staticFilters = useComposeMenuFilters([
    filterByCategory ? `kind:"${category}" OR root_types:"${category}"` : '',
    filterByFeaturedCategory ? featuredFilter : '',
    filterByBuyAgain ? purchasedProductFilter : '',
  ]);

  const customLabels = store.custom_product_type_labels;
  const categoryTitle =
    (customLabels && customLabels[category]) ||
    sentenceCase(category === 'all' ? 'all products' : category);

  const pageTitle =
    hasFeaturedRow && featuredTitle.length ? featuredTitle : categoryTitle;

  const disableAds =
    (hasFeaturedRow && !isJaneDefined) || hideSponsored === 'true';

  const categoryDescription =
    (appMode === 'default' && CATEGORY_DESCRIPTIONS[category]) || '';

  const featuredRowUrl = rowIdParam
    ? paths.menuRow({ id: Number(rowIdParam), title: featuredTitle })
    : paths.menuFeaturedCategory(featuredCategory || '');

  const breadcrumbs = [
    { title: store.name, url: paths.menu() },
    ...(hasFeaturedRow
      ? [{ title: categoryTitle, url: paths.menuCategory(category) }]
      : []),
  ];

  const nextBreadcrumbs = [
    ...insertIfArr(!!category, {
      title:
        category === 'all'
          ? 'All products'
          : customLabels?.[category] || sentenceCase(category || ''),
      url: paths.menuCategory(category),
    }),
    ...insertIfArr(hasFeaturedRow, {
      title: featuredTitle,
      url: featuredRowUrl,
    }),
  ];

  if (
    (hasFeaturedRow && !featuredTitle.length) ||
    (category && !MENU_CATEGORY_TABS[category])
  ) {
    return (
      <Flex flexDirection="column" alignItems="center" p={isMobile ? 24 : 64}>
        <Typography variant="h2-semibold" mb={24} textAlign="center">
          Oops, we can't seem to find this page.
        </Typography>
        <Button to={paths.menu()} label={`Return to ${store.name}`} />
      </Flex>
    );
  }

  return (
    <Flex flexDirection="column" gap={isMobile ? 24 : 48}>
      <MenuPageSeo
        title={pageTitle === 'All products' ? 'all' : pageTitle}
        path={hasFeaturedRow ? featuredRowUrl : paths.menuCategory(category)}
      />
      {hasFeaturedRow && (
        <MenuBreadcrumbs
          breadcrumbs={breadcrumbs}
          title={featuredTitle}
          px={64}
        />
      )}
      <MenuTitle
        title={pageTitle}
        description={categoryDescription}
        px={isMobile ? 24 : 64}
      />
      <MenuFiltersAndProductsGrid
        breadcrumbs={nextBreadcrumbs}
        disableAds={disableAds}
        enableMidGridRow={Boolean(
          category && !['featured', 'specials', 'jane-gold'].includes(category)
        )}
        hasPurchasedProducts={purchasedProductFilter.length > 0}
        menuCategory={category}
        pageTitle={pageTitle}
        staticFilters={staticFilters}
        weightFilter={weightFilter}
      />
    </Flex>
  );
};
